import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---
export interface SearchUserResult {
  userId: string;
  email: string;
  name: string;
  profileImage: string | null;
  // CHG-133 Step 7 follow-up
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface InvitationItem {
  invitationId: string;
  projectId: string;
  inviterId: string;
  inviteeId: string | null;
  inviteeEmail: string;
  role: string;
  status: string;
  token: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  inviter: { id: string; name: string; email: string };
  invitee: { id: string; name: string; email: string; avatarUrl: string | null } | null;
  acceptUrl?: string;
  emailSent?: boolean;
}

// --- API calls ---

export async function searchUsers(query: string, projectId: string, limit = 10) {
  const res = await api.get<ApiResponse<SearchUserResult[]>>(
    `/users/search?q=${encodeURIComponent(query)}&projectId=${encodeURIComponent(projectId)}&limit=${limit}`,
  );
  return res.data.data ?? [];
}

export interface CreateInvitationResult {
  directAdded: boolean;
  emailSent?: boolean;
  [key: string]: unknown;
}

export async function createInvitation(projectId: string, email: string, role = 'MEMBER') {
  const res = await api.post<ApiResponse<CreateInvitationResult>>(
    `/projects/${projectId}/invitations`,
    { email, role },
  );
  return res.data.data!;
}

export async function fetchInvitations(projectId: string) {
  const res = await api.get<ApiResponse<InvitationItem[]>>(
    `/projects/${projectId}/invitations`,
  );
  return res.data.data ?? [];
}

export async function cancelInvitation(projectId: string, invitationId: string) {
  await api.delete(`/projects/${projectId}/invitations/${invitationId}`);
}

export async function resendInvitation(projectId: string, invitationId: string) {
  const res = await api.post<ApiResponse<InvitationItem>>(
    `/projects/${projectId}/invitations/${invitationId}/resend`,
  );
  return res.data.data!;
}

// --- 토큰 기반 API (초대 수락/거절/가입) ---

export interface InvitationDetail {
  invitationId: string;
  status: 'PENDING' | 'EXPIRED' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';
  role: string;
  expiresAt: string;
  isExpired: boolean;
  project: { projectId: string; name: string; projectKey: string };
  inviter: { name: string };
  inviteeEmail: string;
  isExistingUser: boolean;
  // CHG-141 REQ-IF-005: 해당 이메일의 가입 사용자 존재 여부 — 클라이언트 분기용
  userExists: boolean;
}

export async function getInvitationByToken(token: string) {
  const res = await api.get<ApiResponse<InvitationDetail>>(`/invitations/${token}`);
  return res.data.data!;
}

export async function acceptInvitation(token: string) {
  const res = await api.post<ApiResponse<{ projectId: string; projectKey: string }>>(`/invitations/${token}/accept`);
  return res.data.data!;
}

export async function declineInvitation(token: string) {
  await api.post(`/invitations/${token}/decline`);
}
